/**
 * Discipline coach (UI-REVAMP-PLAN §15, COACH-RULES.md): shared types.
 *
 * The coach is advisory only. Its reducer reads normalized observations (`CoachEvent`) derived
 * from the client trading state, 1-second timer ticks and user dismissals, and produces
 * advisories. It never receives a command sender, so it cannot place, modify or cancel orders
 * (see `coach/isolation.test.ts`).
 */

export type CoachPriority = "P1" | "P2" | "P3";
export type CoachTone = "calm" | "coach" | "blunt";

/** Rules in this batch: event- and time-based only (no money, R, P&L or drawdown). */
export const COACH_RULE_IDS = [
  "dont_overtrade",
  "trade_limit_hit",
  "lost_count",
  "ticker_hopping",
  "flip_flopping",
  "step_away",
  "outside_window",
  "wheres_your_stop",
  "exit_later",
  "let_price_come",
  "window_closing",
  "flat_for_close",
] as const;
export type CoachRuleId = (typeof COACH_RULE_IDS)[number];

/** One advisory the coach fired. Snake-case fields are the advisory contract. */
export type Advisory = {
  /** Unique per account: `${rule_id}@${fired_at}`. */
  id: string;
  rule_id: CoachRuleId;
  priority: CoachPriority;
  title: string;
  text: string;
  /** Short factual context ("5 of 6 trades today"), or "". */
  detail: string;
  account_id: string;
  login_id: string;
  fired_at: number;
  /** How bad the signal was when it fired (a count); a dismissed P2 returns only if worse. */
  severity: number;
};

export type OrderSide = "buy" | "sell";
export type OrderKind = "market" | "limit" | "stop" | "other";

type Base = { accountId: string; loginId: string; atMs: number };

/**
 * Normalized observations (derived by `observe.ts` from successive trading states). Identity for
 * replay safety: `fill` by fill id, `submit` / `cancel` by order id, `modify` by order id + the
 * order's update stamp; `position` and `protection` are state, compared with what the coach last
 * saw for that (account, symbol). A position the coach has never seen counts from flat, unless
 * the event is a `baseline`.
 */
export type CoachEvent =
  | (Base & { kind: "fill"; id: string; symbol: string })
  | (Base & { kind: "submit"; id: string; symbol: string; side: OrderSide; orderKind: OrderKind })
  | (Base & {
      kind: "cancel";
      id: string;
      symbol: string;
      side: OrderSide;
      orderKind: OrderKind;
      resting: boolean;
    })
  | (Base & { kind: "modify"; id: string; rev: string; symbol: string })
  /**
   * `baseline`: the quantity the coach starts from (bootstrap), never an entry. `anchor`: the
   * newest fill id of the position when the quantity changed; one fill opens at most one entry,
   * so a reset + replay of the same frames cannot count it again.
   */
  | (Base & { kind: "position"; symbol: string; netQty: number; baseline?: boolean; anchor?: string })
  | (Base & { kind: "protection"; symbol: string; hasStop: boolean; hasTarget: boolean });

/** The account an observation or advisory belongs to: Rithmic login + account id. */
export function coachAccountKey(loginId: string, accountId: string): string {
  return `${encodeURIComponent(loginId)}:${encodeURIComponent(accountId)}`;
}
